// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { DealerModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { styledBoxStyle } from "@keller/App/Components/Common/StyledBox";
import Search from "@keller/XDL/Icons/Search";
import { parseBoolean, xScreen } from "@keller/XDL/Utilities";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    dealers?: DealerModel[];
    industries?: string[];
    hasShowroomLocations?: boolean;
    updateFilteredDealers: (currentFilteredLocations: DealerModel[] | undefined) => void;
}

interface AllLocationsFilterFormStyles {
    form?: InjectableCss;
    gridContainer?: GridContainerProps;
    searchCityNameGridItem?: GridItemProps;
    searchCityNameText?: TextFieldPresentationProps;
    searchButtonGridItem?: GridItemProps;
    searchButton?: ButtonPresentationProps;
    searchIndustryGridItem?: GridItemProps;
    searchShowroomGridItem?: GridItemProps;
}

const allLocationsFilterFormStyles: AllLocationsFilterFormStyles = {
    form: {
        css: css`
            width: 100%;
            margin-bottom: 35px;
        `,
    },
    gridContainer: {
        gap: 20,
        css: css`
            ${styledBoxStyle}
        `,
    },
    searchCityNameGridItem: {
        width: [12, 12, 5, 4, 4],
    },
    searchCityNameText: {
        iconProps: {
            src: Search,
            color: "common.ui7",
        },
    },
    searchIndustryGridItem: {
        width: [12, 12, 4, 3, 2],
    },
    searchShowroomGridItem: {
        width: [12, 12, 4, 3, 2],
    },
    searchButtonGridItem: {
        width: [12, 12, 12, 2, 2],
        css: css`
            &&& {
                margin-left: auto;
                flex-basis: auto;
                align-self: flex-end;
            }
        `,
    },
    searchButton: {
        sizeVariant: "large",
        css: css`
            margin-top: 20px;
            ${xScreen("lte", "md")} {
                margin-top: 0;
            }
            ${xScreen("lte", "xs")} {
                width: 100%;
            }
        `,
    },
};

const styles = allLocationsFilterFormStyles;
const StyledForm = getStyledWrapper("form");

const AllLocationsFilterForm: FC<OwnProps> = ({ industries, dealers, hasShowroomLocations, updateFilteredDealers }) => {
    const [filter, setFilter] = useState({
        industry: "",
        cityOrName: "",
        showroom: "",
    });

    const handleSearchCityOrNameChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFilter(prevState => ({ ...prevState, cityOrName: event.target.value }));
    };

    const handleIndustryFilterDealers = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setFilter(prevState => ({ ...prevState, industry: event.target.value }));
    };

    const handleShowroomFilter = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setFilter(prevState => ({ ...prevState, showroom: event.target.value }));
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const isFilterEmpty = Object.values(filter).every(item => !!item);
        if (isFilterEmpty) {
            updateFilteredDealers(dealers);
            return;
        }

        const filteredDealers = dealers?.filter(dealer => {
            let isSearchByCityName = true;
            let isSearchByIndustry = true;
            let isSearchByShowroom = true;

            if (filter.cityOrName) {
                const cityName = filter.cityOrName.toLocaleLowerCase();
                isSearchByCityName =
                    dealer.name.toLowerCase().includes(cityName) || dealer.city.toLowerCase().includes(cityName);
            }

            if (filter.industry) {
                isSearchByIndustry = dealer.properties.industry === filter.industry;
            }

            if (filter.showroom && filter.showroom?.toLowerCase() === "showroom") {
                isSearchByShowroom = parseBoolean(dealer.properties.isShowroomLocation);
            }

            return isSearchByCityName && isSearchByIndustry && isSearchByShowroom;
        });

        updateFilteredDealers(filteredDealers);
    };

    return (
        <StyledForm {...styles.form} onSubmit={handleSubmit} noValidate>
            <GridContainer {...styles.gridContainer}>
                <GridItem {...styles.searchCityNameGridItem}>
                    <TextField
                        id="searchGeoLocation"
                        {...styles.searchCityNameText}
                        label={translate("Search by City or Name")}
                        value={filter.cityOrName}
                        onChange={handleSearchCityOrNameChanged}
                        placeholder={translate("Search by City or Name")}
                    />
                </GridItem>
                <GridItem {...styles.searchIndustryGridItem}>
                    <Select
                        value={filter.industry}
                        placeholder={translate("Filter by Industry")}
                        label={translate("Filter by Industry")}
                        onChange={handleIndustryFilterDealers}
                    >
                        <option value="">{translate("Filter by Industry")}</option>
                        {industries?.map(industry => (
                            <option key={industry} value={industry}>
                                {industry}
                            </option>
                        ))}
                    </Select>
                </GridItem>
                {hasShowroomLocations && (
                    <GridItem {...styles.searchShowroomGridItem}>
                        <Select
                            value={filter.showroom}
                            placeholder={translate("Filter by Locations")}
                            label={translate("Filter by Location")}
                            onChange={handleShowroomFilter}
                        >
                            <option value="">{translate("All")}</option>
                            <option value="Showroom">{translate("Showroom")}</option>
                        </Select>
                    </GridItem>
                )}
                <GridItem {...styles.searchButtonGridItem}>
                    <Button {...styles.searchButton} type="submit">
                        {translate("Search")}
                    </Button>
                </GridItem>
            </GridContainer>
        </StyledForm>
    );
};

export default AllLocationsFilterForm;
